import React, { useState, useReducer, useEffect } from "react";

import {
    Box,
    Divider,
    Grid,
    Typography,
    Button,
    IconButton,
    styled,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Badge,
    Tooltip
} from '@mui/material';

import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useSystemUtils } from "../../../hooks/useSystemUtils";
import { useSystemRoutes, routeTypes } from "../../../hooks/useSystemRoutes";
import { useDocuments, documentTypes, targetTypes as targetDocumentTypes } from "../../../hooks/api/useDocuments";
import { useLocations, locationTypes, targetTypes } from "../../../hooks/api/useLocations";
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useAppContextFeatures } from "../../../context/AppContext";
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from "../../../hooks/usePermissions";
import { useTranslations } from '../../../hooks/useTranslations';

import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonBreadcumbs from "../../common/CommonBreadcumbs";
import CommonRestricted from "../../common/CommonRestricted";
import { CommonIcons, icons } from "../../common/CommonIcons";

import {
    documentTypes as validationDocumentTypes,
    targetTypes as validationTargetTypes,
    actionTypes as validationActionTypes
} from "../../../hooks/api/useDocumentValidation";

import VerifyOutSaved from "../../@documentValidation/verifyOut/VerifyOutSaved";
import VerifyOutNew from "../../@documentValidation/verifyOut/VerifyOutNew";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3)
}));

const StyledLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem',
    fontWeight: 800
}))

const StyledContent = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem'
}))

const StyledContent2 = styled(Typography)(({ theme }) => ({
    fontSize: '0.8rem',
    mt: 2
}))

const StyledDivider = styled(Divider)(({ theme }) => ({
    mt: 1,
    mb: 0,
    borderStyle: 'dashed'
}))

const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -3,
        top: 13,
        border: `2px solid ${theme.palette.background.paper}`,
        padding: '0 4px',
    },
}));

export default function VerifyOutReplacementDelivery({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@document"] })

    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams()

    const { setSystemLoading, openCustomDrawer, closeCustomDrawer } = useAppContextFeatures();

    const { promptMessage } = useSystemPrompt();
    const { getRoute } = useSystemRoutes(routeType)
    const { getHtmlHead } = useSystemHead(routeType)

    const { get: getDocument } = useDocuments({ 
        documentType: documentTypes.replacementDelivery, 
        targetType: targetDocumentTypes.replacementDelivery
    });
    const { list: getLocationRacks } = useLocations({ locationType: locationTypes.rack, targetType: targetTypes.rack });
    const { formatCurrencyDisplay } = useSystemUtils();

    const { isPermissionAllow } = usePermissions(routeType, scopeIds.document.id, scopeTypes.document.replacementDelivery.id)

    const navigate = useNavigate();

    const [data, setData] = useState()
    const [rackData, setRackData] = useState()

    const fetchData = async () => {

        setSystemLoading(true);

        try {

            let response = await getDocument({ id });

            // console.log('response?.data?.items', response?.data)
            setData(response?.data)

            const locationId = response?.data?.document?.location_id;

            const props = {
                limit: 10000,
                skip: 0,
                sortInfo: {
                    id: "rack_name",
                    dir: -1
                }
            }

            props.parentId = locationId
            props.locationType = locationTypes.rack;

            const { data } = await getLocationRacks(props)
            // console.log('warehouseRacks', warehouseRacks, params)

            // for(let i = 0; i < data.racks.length; i+=1){
            data.racks.map(item => {

                let b = item?.slots;

                let newSlots = [];

                if (b) {

                    Object.keys(b).map((item2, index2) => {
                        // console.log('value', item2)
                        newSlots.push({
                            id: item2,
                            name: b[item2]
                        })
                        return false;
                    })
                }

                item._slots = newSlots;

                return false;
            })

            // console.log(data.racks)

            setRackData(data.racks)

        } catch (error) {

            promptMessage(false, `${error.message}`)
        }

        setSystemLoading(false)

    }

    useEffect(() => {

        fetchData();

    }, [])

    // const handleChangeTab = (event, newValue) => {
    //     console.log('value', newValue, event)
    //     setTabValue(newValue);
    // };

    const RenderDrawerSaved = ({ row, handleOnSuccess }) => {

        let enableEdit = true

        // check permission
        if (isPermissionAllow({ functionType: functionTypes.verifyOutUndo.id }) === false)
            enableEdit = false

        return (
            <>
                {
                    VerifyOutSaved({
                        row,
                        enableEdit,
                        documentType: validationDocumentTypes.replacementDelivery,
                        targetType: validationTargetTypes.replacementDelivery,
                        actionType: validationActionTypes.verifyOut.replacementDelivery,
                        // handleOnSuccess
                    })
                }
            </>
        )
    }

    const RenderDrawerNew = ({ row, rackData, handleOnSuccess }) => {
        return (
            <>
                {
                    VerifyOutNew({
                        locationId: data?.document?.location_id,
                        row,
                        rackData,
                        documentType: validationDocumentTypes.replacementDelivery,
                        targetType: validationTargetTypes.replacementDelivery,
                        actionType: validationActionTypes.verifyOut.replacementDelivery,
                        handleOnSuccess
                    })
                }
            </>
        )
    }

    const openSaved = ({ row, rackData }) => {
        openCustomDrawer({
            show: true,
            anchor: "right",
            title: t(`savedValidation`),
            PaperProps: {
                sx: { width: 5 / 6 }
            },
            handleOnClose: () => { fetchData() },
            ComponentActions:
                <>
                    <Button
                        variant="contained"
                        // color={"primary"}
                        size="small"
                        onClick={() => openNew({ row, rackData })}
                    >
                        {t('newValidation')}
                    </Button>
                </>,
            ComponentBody:
                <>
                    <RenderDrawerSaved
                        row={row}
                        handleOnSuccess={() => {
                            closeCustomDrawer();
                            fetchData()
                        }}
                    />
                </>
        })
    }

    const openNew = ({ row, rackData }) => {

        openCustomDrawer({
            show: true,
            anchor: "right",
            title: t(`addNewValidation`),
            PaperProps: {
                sx: { width: 2 / 3 }
            },
            handleOnClose: () => { fetchData() },
            ComponentActions:
                <Button
                    variant="contained"
                    // color={"primary"}
                    size="small"
                    onClick={() => openSaved({ row, rackData })}
                >
                   {t('viewSavedValidation')}
                </Button>,
            ComponentBody:
                <RenderDrawerNew
                    row={row}
                    rackData={rackData}
                    handleOnSuccess={() => {
                        closeCustomDrawer();
                        fetchData()
                    }}
                />
        })

    }

    const RenderButtonLogs = ({ row }) => {

        // check permission
        if (isPermissionAllow({ functionType: functionTypes.verifyOutLogs.id }) === false) return <></>

        return (
            <Tooltip title={`${row?.verify_out_uids ? row.verify_out_uids.length : "0"} ${t('validationSaved')}`}>
                <IconButton
                    size="small"
                    color={"primary"}
                    onClick={() => {

                        openSaved({ row, rackData })
                    }}
                >

                    <StyledBadge
                        badgeContent={
                            row?.verify_out_uids ? row.verify_out_uids.length : "0"
                        }
                        color="secondary"
                    >
                        <CommonIcons name={icons.history} />
                    </StyledBadge>
                </IconButton>
            </Tooltip>
        )
    }

    const RenderButtonNew = ({ row }) => {

        // check permission
        if (isPermissionAllow({ functionType: functionTypes.verifyOutNew.id }) === false) return <></>

        return (
            <Tooltip title={t(`newValidation`)}>
                <IconButton
                    size="small"
                    color={"primary"}
                    onClick={() => {
                        openNew({ row, rackData })
                    }}
                >
                    <CommonIcons name={icons.add} />
                </IconButton>
            </Tooltip>
        )
    }

    const RenderTableRow = ({ row, rackData, viewOnly }) => {
        return (
            <>
                <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                    <TableCell>{(row.index + 1)}</TableCell>
                    <TableCell component="th" scope="row">
                        {row.item_name}
                    </TableCell>
                    <TableCell>{row.code}</TableCell>
                    <TableCell align="right">
                        {row.verify_out_qty} / {row.qty} {row.uom_name}
                    </TableCell>
                    <TableCell align="right">

                        {
                            parseFloat(row.unverify_out_qty) > 0 ?
                                (
                                    <Chip
                                        icon={
                                            <CommonIcons name={icons.warning} sx={{ ml: 1 }} />
                                        }
                                        label={
                                            `${t('required')} ${row.unverify_out_qty}`
                                        }
                                        color='warning'
                                        variant="contained"
                                    // sx={{
                                    //     width: '100%'
                                    // }}
                                    // size="small"
                                    />
                                )
                                :
                                (
                                    <Chip
                                        icon={
                                            <CommonIcons name={icons.tick} sx={{ ml: 1 }} />
                                        }
                                        label={t(`completed`)}
                                        color='primary'
                                        variant="contained"
                                    // sx={{
                                    //     width: '100%'
                                    // }}
                                    // size="small"
                                    />
                                )
                        }

                    </TableCell>
                    <TableCell align="right">
                        <RenderButtonLogs
                            row={row}
                        />
                        <RenderButtonNew
                            row={row}
                        />
                    </TableCell>
                </TableRow>
            </>
        )
    }

    const RenderTable = () => {

        if (!data) return <></>

        return (
            <TableContainer
                // component={Paper}
                sx={{
                    // minHeight: 400,
                    // maxHeight: 400
                }}
            >
                <Table
                    size="small"
                    stickyHeader
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>{t('name')}</TableCell>
                            {/* <TableCell>Description</TableCell> */}
                            <TableCell>{t('code')}</TableCell>
                            <TableCell align="right">{t('qty')}</TableCell>
                            <TableCell align="right">{t('balance')}</TableCell>
                            <TableCell align="right"><></></TableCell>
                            {/* <TableCell align="right">Unit</TableCell> */}
                            {/* <TableCell align="right">Sum</TableCell> */}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            data?.items.map((row, index) => (
                                <RenderTableRow
                                    key={index}
                                    row={{ ...row, index }}
                                    rackData={rackData}
                                />
                            ))
                        }

                    </TableBody>
                </Table>
            </TableContainer>
        )
    }

    const RenderFormProfile = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`profile`)}
                            subTitle={t(`relatedProfilesAttachedToDocument`)}
                        />

                    </Grid>

                    <Grid item xs={12}>
                        <StyledDivider />
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('location')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document?.location_name}
                                </StyledContent>
                            </Grid>

                        </Grid>
                    </Grid>

                    <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                        <StyledLabel>{t('placeBy')}</StyledLabel>
                    </Grid>
                    <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                        <StyledContent>
                            {data?.document?.profile_name} ({data?.document?.code})
                        </StyledContent>
                        <StyledContent2>
                            {data?.document?.address_1} {data?.document?.address_2} {data?.document?.postcode}, {data?.document?.area} {data?.document?.country_name}
                        </StyledContent2>
                        <StyledContent2>
                            {data?.document?.contact} / {data?.document?.email}
                        </StyledContent2>
                    </Grid>

                    <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                        <StyledLabel>{t('billTo')}</StyledLabel>
                    </Grid>
                    <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                        <StyledContent>
                            {data?.document?.billing_profile_name} ({data?.document?.billing_code})
                        </StyledContent>
                        {/* <StyledContent>
                                    {data?.document?.billing_address_1} {data?.document?.billing_address_2} {data?.document?.billing_postcode}, 
                                    {data?.document?.billing_area_name} {data?.document?.billing_country_name}
                                </StyledContent> */}
                    </Grid>

                    <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                        <StyledLabel>{t('shipTo')}</StyledLabel>
                    </Grid>
                    <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                        <StyledContent>
                            {data?.document?.delivery_profile_name} ({data?.document?.delivery_code})
                        </StyledContent>
                        {/* <StyledContent>
                                    {data?.document?.delivery_address_1} {data?.document?.delivery_address_2} {data?.document?.delivery_postcode}, 
                                    {data?.document?.delivery_area_name} {data?.document?.delivery_country_name}
                                </StyledContent> */}
                    </Grid>

                </Grid>
            </StyledCard>
        )
    }

    const RenderFormDocument = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`document`)}
                            subTitle={t(`basicInformationAboutTheDocument`)}
                        />

                    </Grid>
                    <Grid item xs={12}>
                        <StyledDivider />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('documentNo')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.doc_number}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('documentDate')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.doc_date}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    {t('remark')}
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.doc_remark}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    {t('billing')}
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.type}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    {t('collection')}
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.is_payment_collect === "1" ? 'Required' : 'No'} ({data?.document.collection_amount} / {data?.document.require_collect_amount})
                                </StyledContent>
                                <StyledContent>
                                    {data?.collection_remark}
                                </StyledContent>
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderForm = () => {
        return (
            <>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={7} textAlign={"left"}>
                        {
                            RenderFormDocument()
                        }
                    </Grid>
                    <Grid item xs={12} sm={12} md={5} textAlign={"left"}>
                        {
                            RenderFormProfile()
                        }
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        textAlign: 'left',
                        mt: 3,
                        pl: 0.5,
                    }}
                >
                    <CommonSectionHeader
                        title={t(`items`)}
                        subTitle={t(`detailInformationAboutTheItemsInvolved`)}
                    />
                </Box>

                <StyledCard
                    sx={{
                        mt: 1.5,
                        p: 0
                    }}
                >
                    <Grid container rowSpacing={0}>

                        <RenderTable />

                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"center"}>
                        <Button
                            type="button"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            // loading={isLoading}
                            onClick={() => navigate(-1)}
                        >
                            {t('back')}
                        </Button>
                    </Grid>
                </Grid>
            </>
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.verifyOutReplacementDelivery })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={`${t('verifyOut')} ${data?.document.doc_number}`}
                breadcrumbs={[
                    {
                        displayText: t("replacementDeliveries"),
                        props: {
                            href: getRoute("documents/replacementDeliveries")
                        }
                    },
                    {
                        displayText: `#${data?.document.doc_number}`,
                        props: {
                            color: 'secondary.main',
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageVerifyOut.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}